import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import raceApi from '../../../clients/RaceClient';
import { useAuth } from '../../../context/AuthContext';
import PageBreadcrumb from '../../shared/PageBreadcrumb';
import ButtonSelect from '../../shared/ButtonSelect';
import BookingCalendar from '../shared/BookingCalendar';
import { normalizeLocationKey } from '../../../clients/locationApiBase';
import identityApi from '../../../clients/IdentityClient';
import { resolvePreferredLocation, savePreferredLocationIfEmpty } from '../../../utils/preferredLocation';
import { formatLocalDate } from '../../../utils/time';
import { formatUsTime } from '../../../utils/bookingUtils';
import type { ActivityOption } from '../../../models/entitlement/ActivityOption';
import type { SlotOption } from '../../../models/entitlement/SlotOption';
import type { RedeemEntitlementRequestBody } from '../../../models/requestBodies/RedeemEntitlementRequestBody';
import { mapRedeemFailure } from './redeemErrorMapping';
import '../BookingCreate.css';

const VENUES: { key: string; label: string }[] = [
  { key: 'pasadena', label: 'Pasadena' },
  { key: 'tustin', label: 'Tustin' },
  { key: 'kansascity', label: 'Kansas City' },
  { key: 'phoenix', label: 'Phoenix' },
];

type RigClass = 'pro' | 'esports';
const RIG_CLASSES: RigClass[] = ['pro', 'esports'];
const rigLabel = (r: RigClass) => (r === 'pro' ? 'Pro' : 'Esports');

const activityLabel = (a: ActivityOption): string => a.title;

// Max book-ahead in days, derived from the 48h window. The calendar disables dates
// beyond this so the "48 hours ahead" 422 rarely fires; the server still enforces it.
const maxDate = (): string => {
  const d = new Date();
  d.setDate(d.getDate() + 2);
  return formatLocalDate(d);
};

const EntitlementBooking: React.FC = () => {
  const { user, refreshUserProfile } = useAuth();
  const [location, setLocation] = useState(() =>
    resolvePreferredLocation(
      user?.preferredLocation,
      (key) => VENUES.some((v) => v.key === key),
      normalizeLocationKey,
    ),
  );
  const [activities, setActivities] = useState<ActivityOption[]>([]);
  const [activityId, setActivityId] = useState('');
  const [date, setDate] = useState(() => formatLocalDate(new Date()));
  const [slots, setSlots] = useState<SlotOption[]>([]);
  const [slotId, setSlotId] = useState('');
  const [rigClass, setRigClass] = useState<RigClass>('pro');
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success'>('idle');
  const [confirmation, setConfirmation] = useState('');
  const [failure, setFailure] = useState<ReturnType<typeof mapRedeemFailure> | null>(null);

  // Load activities for the location and auto-select the first one (mirrors the credit
  // flow). Clears the selection when the location has no activities.
  useEffect(() => {
    if (!location) return;
    setSlots([]); setSlotId('');
    raceApi.getActivities(location)
      .then((next) => {
        setActivities(next);
        setActivityId(next.length > 0 ? next[0].activityId : '');
      })
      .catch(() => { setActivities([]); setActivityId(''); });
  }, [location]);

  const loadSlots = React.useCallback(() => {
    if (!location || !activityId || !date) { setSlots([]); return; }
    setIsLoadingSlots(true);
    raceApi.getDayAvailability(location, date, activityId)
      .then(setSlots)
      .catch(() => setSlots([]))
      .finally(() => setIsLoadingSlots(false));
  }, [location, activityId, date]);

  useEffect(() => { loadSlots(); }, [loadSlots]);

  const activity = activities.find((a) => a.activityId === activityId);
  const visibleSlots = slots.filter((s) => (rigClass === 'pro' ? s.proRigsAvailable : s.esportsRigsAvailable) > 0);
  const canConfirm = Boolean(location && activity && date && slotId) && status !== 'loading' && status !== 'success';

  const onConfirm = async () => {
    if (!activity || !user) return;
    setStatus('loading'); setFailure(null);
    const body: RedeemEntitlementRequestBody = {
      location, date, slotId, experienceId: activity.experienceId, activityId: activity.activityId,
      firstName: user.firstName ?? '', lastName: user.lastName ?? '', email: user.email ?? '',
      phone: user.phone ?? '', rigClass, experienceLevel: 'expert',
    };
    const result = await raceApi.redeemEntitlement(location, body);
    if (result.ok) {
      setStatus('success');
      setConfirmation(result.booking.confirmationCode ?? String(result.booking.id));
      await savePreferredLocationIfEmpty(
        user.preferredLocation, location,
        (loc) => identityApi.patchUser(loc),
        refreshUserProfile,
      );
      return;
    }
    const ux = mapRedeemFailure(result.status, result.error);
    setFailure(ux); setStatus('idle');
    if (ux.refetchAvailability) { setSlotId(''); loadSlots(); }
  };

  if (status === 'success') {
    return (
      <>
        <PageBreadcrumb items={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'Book a session' }]} />
        <div className="container py-4">
          <div className="alert alert-success" role="alert">
            <h4>Booked!</h4>
            <p className="mb-0">Confirmation code: <strong>{confirmation}</strong></p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageBreadcrumb items={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'Book a session' }]} />
      <div className="container py-4">
        <div className="booking-create-content">
          <div className="row g-4">
            <div className="col-12 col-lg-5">
              <div className="d-flex flex-column gap-3">
                <div>
                  <ButtonSelect
                    options={VENUES.map((v) => v.key)}
                    selected={location}
                    onChange={setLocation}
                    title="Location:"
                    renderLabel={(key) => VENUES.find((v) => v.key === key)?.label ?? key}
                  />
                </div>

                <div>
                  <ButtonSelect
                    options={activities.map((a) => a.activityId)}
                    selected={activityId}
                    onChange={(id) => { setActivityId(id); setSlotId(''); }}
                    disabled={activities.length === 0}
                    title="Select activity:"
                    renderLabel={(id) => {
                      const a = activities.find((x) => x.activityId === id);
                      return a ? activityLabel(a) : id;
                    }}
                    buttonClassName="d-block w-100 text-start"
                  />
                </div>

                <div>
                  <ButtonSelect
                    options={RIG_CLASSES}
                    selected={rigClass}
                    onChange={(r) => { setRigClass(r); setSlotId(''); }}
                    title="Your seat:"
                    renderLabel={rigLabel}
                  />
                </div>
              </div>
            </div>

            <div className="col-12 col-lg-7">
              <label className="settings-field-label">Date:</label>
              <BookingCalendar
                selectedDate={date}
                onSelectDate={(iso) => { setDate(iso); setSlotId(''); }}
                isDateDisabled={(iso) => iso > maxDate()}
              />

              <label className="settings-field-label">Time:</label>
              {isLoadingSlots ? (
                <p className="text-paragraphs mt-1">Loading available times…</p>
              ) : visibleSlots.length === 0 ? (
                <div className="alert alert-warning mt-1" role="alert">
                  No available times for this selection — try another date or seat type.
                </div>
              ) : (
                <div className="booking-time-slots mt-1">
                  {visibleSlots.map((s) => (
                    <button
                      key={s.slotId}
                      type="button"
                      className={`btn ${slotId === s.slotId ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => setSlotId(s.slotId)}
                    >
                      {formatUsTime(s.startTime)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {failure && (
            <div className="alert alert-warning mt-3" role="alert">
              {failure.message}
              {failure.offerPaidBooking && (
                <> <Link to="/booking" className="alert-link">Book with credits instead</Link></>
              )}
            </div>
          )}

          <div className="d-flex justify-content-end align-items-center gap-3 mt-4">
            <span className="text-success">$0 — covered by your pass</span>
            <button
              type="button"
              className="btn btn-success booking-wizard-nav-button"
              onClick={onConfirm}
              disabled={!canConfirm}
            >
              {status === 'loading' ? 'Booking…' : 'Confirm'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default EntitlementBooking;
